import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { apiUrl } from "../utils/api";
import { BRAND } from "../components/risikoanalyseStep/tokens";
import { sectionColorHex } from "../components/risikoanalyseStep/sectionColor";
import TopBar from "../components/risikoanalyseStep/TopBar";
import Sidebar from "../components/risikoanalyseStep/Sidebar";
import TocMenu from "../components/risikoanalyseStep/TocMenu";
import {
  useTocLayout,
  type TocMode,
} from "../components/risikoanalyseStep/useTocLayout";
import BottomBar from "../components/risikoanalyseStep/BottomBar";
import {
  scrollFormToTop,
  scrollScrollableAncestorsToTop,
} from "../components/risikoanalyseStep/scrollToTop";
import KennzahlenWelcomeFlow from "../components/risikoKennzahlenStep/KennzahlenWelcomeFlow";
import PeriodPicker from "../components/risikoKennzahlenStep/PeriodPicker";
import CurrencyInput from "../components/risikoKennzahlenStep/CurrencyInput";
import EmployeeMatrixGrid from "../components/risikoKennzahlenStep/EmployeeMatrixGrid";
import StepHero, {
  type KennzahlenHeroKind,
} from "../components/risikoKennzahlenStep/StepHero";
import {
  BASIS_FIELDS,
  RISIKO_FIELDS,
  MITARBEITER_FIELDS,
  isCurrencyField,
  isNumericField,
} from "../components/risikoKennzahlenStep/schema";
import type {
  KennzahlenAnswers,
  KennzahlenStep,
  YearSection,
} from "../components/risikoKennzahlenStep/types";
import type {
  SectionColor,
  SectionDef,
  SubStepDef,
} from "../components/risikoanalyseStep/types";

const WELCOME_LS_PREFIX = "risikokennzahlen-welcome-seen:";
const DRAFT_LS_PREFIX = "risikokennzahlen-draft:";
const SIDEBAR_WIDTH = 288;
const BOTTOM_BAR_RESERVE = 110;
const AUTOSAVE_DEBOUNCE_MS = 1500;

const useStyles = makeStyles({
  pageState: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: BRAND.ink,
    color: BRAND.white,
    fontFamily: BRAND.fontPrimary,
  },
  pageStateCard: {
    maxWidth: "480px",
    paddingTop: "32px",
    paddingBottom: "32px",
    paddingLeft: "24px",
    paddingRight: "24px",
    backgroundColor: "rgba(255,255,255,0.05)",
    textAlign: "center",
    ...shorthands.borderRadius("8px"),
  },
  pageStateTitle: {
    margin: 0,
    marginBottom: "8px",
    fontFamily: BRAND.fontAccent,
    fontSize: "1.8rem",
    color: BRAND.gold,
  },
  pageStateBody: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: "rgba(255,255,255,0.8)",
  },
  shell: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    backgroundColor: BRAND.bg,
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
  },
  shellEmbedded: {
    minHeight: "auto",
  },
  body: {
    flexGrow: 1,
    display: "flex",
    overflow: "hidden",
  },
  bodyEmbedded: {
    overflow: "visible",
  },
  /**
   * Ob das Verzeichnis dauerhaft links steht, entscheidet die gemessene
   * CONTAINER-Breite (`useTocLayout`) — nicht mehr eine Viewport-Media-Query.
   * Im schwebenden Fenster war die schlicht falsch: der Viewport ist breit,
   * das Fenster nicht, und deshalb fehlte hier bis #1312/#1313 jede Navigation.
   */
  sidebarDesktop: {
    // Flex-Spalte statt `block`: das `<nav>` der Sidebar wächst darin per
    // `flex-grow` auf die volle Höhe — auch dort, wo `height: 100%` mangels
    // definierter Elternhöhe zu `auto` auflöst.
    display: "flex",
    flexDirection: "column",
    flexShrink: 0,
    width: `${SIDEBAR_WIDTH}px`,
    ...shorthands.borderRight("1px", "solid", "rgba(45,20,43,0.1)"),
  },
  /**
   * Eingebettet scrollt der Fenster-Body, nicht `main` — die Sidebar klebt
   * deshalb am oberen Rand. Ihre Höhe kommt als gemessener Wert dazu
   * (`availableHeight`, inline gesetzt): ohne den stünde das `<aside>` auf
   * Inhaltshöhe und die dunkle Leiste hörte mitten im Formular auf.
   */
  sidebarSticky: {
    position: "sticky",
    top: 0,
    alignSelf: "flex-start",
  },
  main: {
    flexGrow: 1,
    display: "flex",
    flexDirection: "column",
    overflowY: "auto",
  },
  mainEmbedded: {
    overflowY: "visible",
  },
  inner: {
    width: "100%",
    maxWidth: "880px",
    marginLeft: "auto",
    marginRight: "auto",
    paddingTop: "24px",
    paddingLeft: "16px",
    paddingRight: "16px",
    paddingBottom: `${BOTTOM_BAR_RESERVE}px`,
    flexGrow: 1,
    "@media (min-width: 768px)": {
      paddingLeft: "32px",
      paddingRight: "32px",
    },
  },
  innerEmbedded: {
    paddingBottom: "16px",
  },
  sectionPill: {
    display: "inline-block",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingLeft: "10px",
    paddingRight: "10px",
    fontSize: "0.72rem",
    fontWeight: 700,
    letterSpacing: "0.05em",
    textTransform: "uppercase",
    color: BRAND.white,
    marginBottom: "8px",
    ...shorthands.borderRadius("999px"),
  },
  titleRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    columnGap: "16px",
    flexWrap: "wrap",
  },
  title: {
    margin: 0,
    marginTop: "4px",
    fontFamily: BRAND.fontAccent,
    fontSize: "clamp(1.6rem, 4vw, 2.4rem)",
    fontWeight: 400,
    color: BRAND.ink,
    lineHeight: 1.15,
    flexGrow: 1,
    minWidth: "200px",
  },
  description: {
    marginTop: "8px",
    marginBottom: 0,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.95rem",
    color: BRAND.muted,
  },
  questions: {
    marginTop: "20px",
    display: "flex",
    flexDirection: "column",
    rowGap: "18px",
  },
  fieldBlock: {
    display: "flex",
    flexDirection: "column",
    rowGap: "6px",
  },
  fieldLabel: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1rem",
    fontWeight: 400,
    color: BRAND.ink,
  },
  fieldHint: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.8rem",
    color: BRAND.muted,
  },
  twoCol: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "16px",
    "@media (min-width: 720px)": {
      gridTemplateColumns: "1fr 1fr",
    },
  },
  matrixWrap: {
    display: "flex",
    flexDirection: "column",
    rowGap: "12px",
  },
  matrixIntro: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.92rem",
    color: BRAND.ink,
  },
  bottomBarFixed: {
    position: "fixed",
    bottom: 0,
    left: 0,
    right: 0,
    zIndex: 15,
  },
  /** Eingerückt, solange das Verzeichnis links steht. */
  bottomBarFixedNebenSidebar: {
    left: `${SIDEBAR_WIDTH}px`,
  },
  // Summary
  summaryGrid: {
    display: "flex",
    flexDirection: "column",
    rowGap: "16px",
  },
  summaryCard: {
    backgroundColor: BRAND.white,
    ...shorthands.border("1px", "solid", BRAND.borderDashed),
    ...shorthands.borderRadius("10px"),
    paddingTop: "18px",
    paddingBottom: "18px",
    paddingLeft: "20px",
    paddingRight: "20px",
  },
  summaryHead: {
    display: "flex",
    alignItems: "baseline",
    justifyContent: "space-between",
    columnGap: "10px",
    marginBottom: "12px",
  },
  summaryYear: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.4rem",
    color: BRAND.ink,
  },
  summaryEdit: {
    appearance: "none",
    backgroundColor: "transparent",
    backgroundImage: "none",
    cursor: "pointer",
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.85rem",
    fontWeight: 600,
    ...shorthands.border("0"),
    textDecorationLine: "underline",
  },
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(3, 1fr)",
    gap: "12px",
    marginBottom: "10px",
  },
  kpiTile: {
    backgroundColor: BRAND.bg,
    ...shorthands.borderRadius("8px"),
    paddingTop: "10px",
    paddingBottom: "10px",
    paddingLeft: "12px",
    paddingRight: "12px",
  },
  kpiLabel: {
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.7rem",
    fontWeight: 700,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    color: BRAND.muted,
  },
  kpiValue: {
    fontFamily: BRAND.fontAccent,
    fontSize: "1.2rem",
    color: BRAND.ink,
    fontVariantNumeric: "tabular-nums",
    marginTop: "2px",
  },
  rowFact: {
    display: "flex",
    justifyContent: "space-between",
    columnGap: "12px",
    paddingTop: "6px",
    paddingBottom: "6px",
    fontSize: "0.88rem",
    ...shorthands.borderTop("1px", "dashed", BRAND.borderDashed),
  },
  rowFactKey: { color: BRAND.muted },
  rowFactValue: { color: BRAND.ink, fontVariantNumeric: "tabular-nums" },
});

const YEAR_COLORS: SectionColor[] = ["pillarOrange", "blue", "green", "black", "purple"];

const buildSteps = (selectedYears: number[]): KennzahlenStep[] => {
  const steps: KennzahlenStep[] = [
    { kind: "period", key: "period", title: "Erfassungszeitraum" },
  ];
  for (const year of selectedYears) {
    steps.push({
      kind: "year",
      key: `${year}-basis`,
      year,
      section: "basis",
      title: "Basis-Kennzahlen",
    });
    steps.push({
      kind: "year",
      key: `${year}-risiko`,
      year,
      section: "risiko",
      title: "Risiko & Kosten",
    });
    steps.push({
      kind: "year",
      key: `${year}-mitarbeiter`,
      year,
      section: "mitarbeiter",
      title: "Mitarbeiter",
    });
  }
  steps.push({ kind: "summary", key: "summary", title: "Übersicht & Abschluss" });
  return steps;
};

const buildSections = (selectedYears: number[]): SectionDef[] => {
  const periodSub: SubStepDef = {
    key: "period",
    title: "Zeitraum festlegen",
    questions: [],
  };
  const summarySub: SubStepDef = {
    key: "summary",
    title: "Übersicht",
    questions: [],
    customKind: "summary",
  };

  const yearSections: SectionDef[] = selectedYears.map((year, idx) => ({
    key: `year-${year}`,
    title: `${year}`,
    color: YEAR_COLORS[idx % YEAR_COLORS.length],
    subSteps: [
      { key: `${year}-basis`, title: "Basis-Kennzahlen", questions: [] },
      { key: `${year}-risiko`, title: "Risiko & Kosten", questions: [] },
      { key: `${year}-mitarbeiter`, title: "Mitarbeiter", questions: [] },
    ],
  }));

  return [
    {
      key: "period",
      title: "Zeitraum",
      color: "orange",
      subSteps: [periodSub],
    },
    ...yearSections,
    {
      key: "summary",
      title: "Zusammenfassung",
      color: "purple",
      subSteps: [summarySub],
    },
  ];
};

const HERO_BY_KIND: Record<
  "period" | YearSection | "summary",
  KennzahlenHeroKind
> = {
  period: "period",
  basis: "basis",
  risiko: "risiko",
  mitarbeiter: "mitarbeiter",
  summary: "summary",
};

const formatCurrency = (raw: string): string => {
  if (!raw) return "—";
  const num = Number(String(raw).replace(/\./g, "").replace(",", "."));
  if (!Number.isFinite(num)) return raw;
  return num.toLocaleString("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });
};

const isAnswered = (val: string | undefined): boolean =>
  typeof val === "string" && val.trim() !== "";

const computeProgress = (
  selectedYears: number[],
  answers: KennzahlenAnswers
): { total: number; answered: number } => {
  let total = 0;
  let answered = 0;
  const fieldGroups = [BASIS_FIELDS, RISIKO_FIELDS, MITARBEITER_FIELDS];
  for (const year of selectedYears) {
    for (const group of fieldGroups) {
      for (const f of group) {
        total += 1;
        if (isAnswered(answers[`${f.name}_${year}`])) answered += 1;
      }
    }
  }
  return { total, answered };
};

export type RisikoKennzahlenStepFormProps = {
  chrome?: "public" | "embedded";
  /**
   * `"auto"` (Default) — die gemessene Container-Breite entscheidet zwischen
   * dauerhaftem Verzeichnis und Menü-Knopf. `"none"` schaltet das Verzeichnis
   * ganz ab (z. B. `RisikoanalyseDetailFormPublic`, wo heute keins existiert
   * und auch keins auftauchen soll).
   */
  toc?: TocMode;
  initialAnswers?: KennzahlenAnswers;
  onSubmitEmbedded?: (answers: KennzahlenAnswers) => Promise<void> | void;
  embeddedSubmitLabel?: string;
};

const RisikoKennzahlenStepForm = ({
  chrome = "public",
  toc = "auto",
  initialAnswers,
  onSubmitEmbedded,
  embeddedSubmitLabel = "Kennzahlen speichern",
}: RisikoKennzahlenStepFormProps) => {
  const styles = useStyles();
  const { token } = useParams();

  const isPublic = chrome === "public";
  const isEmbedded = chrome === "embedded";
  const draftKey = (t: string) => DRAFT_LS_PREFIX + t;

  const [loading, setLoading] = useState(isPublic);
  const [valid, setValid] = useState(!isPublic);
  const [submitting, setSubmitting] = useState(false);
  const [submitOk, setSubmitOk] = useState(false);

  const [companyName, setCompanyName] = useState("");
  const [answers, setAnswers] = useState<KennzahlenAnswers>(initialAnswers ?? {});
  const [yearsBack, setYearsBack] = useState(1);

  const [showWelcome, setShowWelcome] = useState<boolean>(() => {
    if (!isPublic || !token) return false;
    try {
      return window.localStorage.getItem(WELCOME_LS_PREFIX + token) !== "1";
    } catch {
      return true;
    }
  });

  const [stepIdx, setStepIdx] = useState(0);
  const [savingDraft, setSavingDraft] = useState(false);
  const [draftSavedAt, setDraftSavedAt] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement | null>(null);
  const shellRef = useRef<HTMLDivElement | null>(null);
  /**
   * Dauerhaftes Verzeichnis oder Menü-Knopf? Das entscheidet die gemessene
   * Breite des Shell-Elements — nicht `chrome` und nicht der Viewport.
   * Vollseite/maximiertes Fenster liegen über der Schwelle, ein
   * wiederhergestelltes Fenster (1140 px) darunter.
   */
  const { layout: tocLayout, availableHeight } = useTocLayout(shellRef, toc);
  const autosaveTimerRef = useRef<number | null>(null);
  const draftLoadedRef = useRef(!isPublic);

  const currentYear = useMemo(() => new Date().getFullYear(), []);
  const selectedYears = useMemo(() => {
    const years = [currentYear + 1, currentYear];
    for (let i = 1; i <= yearsBack; i += 1) years.push(currentYear - i);
    return years.slice().sort((a, b) => a - b);
  }, [currentYear, yearsBack]);

  const steps = useMemo(() => buildSteps(selectedYears), [selectedYears]);
  const sections = useMemo(() => buildSections(selectedYears), [selectedYears]);
  const totalSteps = steps.length;

  // Map step index → (sectionIdx, subStepIdx) for the Sidebar
  const { sectionIdx, subStepIdx } = useMemo(() => {
    let s = 0;
    let cursor = 0;
    while (s < sections.length) {
      const len = sections[s].subSteps.length;
      if (stepIdx < cursor + len) {
        return { sectionIdx: s, subStepIdx: stepIdx - cursor };
      }
      cursor += len;
      s += 1;
    }
    return { sectionIdx: 0, subStepIdx: 0 };
  }, [stepIdx, sections]);

  // Public: load token + restore draft
  useEffect(() => {
    if (!isPublic) return;
    const run = async () => {
      if (!token) {
        setValid(false);
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(apiUrl(`public-form/${token}`));
        if (!res.ok) {
          setValid(false);
          return;
        }
        setValid(true);
        try {
          const data = await res.json();
          const c = data?.contact;
          if (c) setCompanyName(c.displayName || c.lastname || "");
        } catch {}

        const draftRaw = window.localStorage.getItem(draftKey(token));
        if (draftRaw) {
          try {
            const parsed = JSON.parse(draftRaw);
            if (parsed && typeof parsed === "object") {
              setAnswers((parsed.answers as KennzahlenAnswers) ?? {});
              if (typeof parsed.yearsBack === "number") setYearsBack(parsed.yearsBack);
              if (typeof parsed.stepIdx === "number") setStepIdx(parsed.stepIdx);
              if (typeof parsed.savedAt === "string") setDraftSavedAt(parsed.savedAt);
            }
          } catch {}
        }
        draftLoadedRef.current = true;
      } catch {
        setValid(false);
      } finally {
        setLoading(false);
      }
    };
    run();
  }, [token, isPublic]);

  // Debounced auto-save (public only)
  useEffect(() => {
    if (!isPublic || !draftLoadedRef.current || !token) return;
    if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = window.setTimeout(() => {
      const payload = {
        answers,
        yearsBack,
        stepIdx,
        savedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(draftKey(token), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch {}
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => {
      if (autosaveTimerRef.current) window.clearTimeout(autosaveTimerRef.current);
    };
  }, [answers, yearsBack, stepIdx, token, isPublic]);

  // Clamp stepIdx if yearsBack shrinks the steps array
  useEffect(() => {
    if (stepIdx >= totalSteps) setStepIdx(totalSteps - 1);
  }, [totalSteps, stepIdx]);

  const setAnswer = (key: string, value: string) => {
    setAnswers((prev) => ({ ...prev, [key]: value }));
  };

  const progress = useMemo(
    () => computeProgress(selectedYears, answers),
    [selectedYears, answers]
  );
  const overallFraction = progress.total === 0 ? 0 : progress.answered / progress.total;
  const stepFraction = (stepIdx + 1) / totalSteps;
  const isLastStep = stepIdx === totalSteps - 1;
  const canPrev = stepIdx > 0;

  /**
   * Nach JEDEM Schrittwechsel steht der Inhalt oben — egal ob Weiter, Zurück
   * oder Klick im Verzeichnis.
   *
   * Vorher stand hier dreierlei Logik in einer Datei: `goPrev`/`goNext`
   * behalfen sich mit `if (isEmbedded) window.scrollTo(...)`, `handleSelect`
   * rief nur `mainRef.current?.scrollTo(...)` — und genau das ist eingebettet
   * wirkungslos, weil `mainEmbedded` auf `overflowY: visible` steht. Ein Sprung
   * aus dem Verzeichnis wechselte also den Schritt, ließ die Ansicht aber auf
   * halber Höhe stehen.
   *
   * Bewusst als Effekt und nicht in den Klick-Handlern (so wie in
   * `RisikoImmobilieStepForm`): `setStepIdx` rendert erst danach, ein Scroll im
   * Handler betrifft also noch den ALTEN Inhalt und wird vom folgenden Render
   * abgeschnitten. Drei Container, weil das Formular in drei Rahmen läuft:
   * `main` (öffentliche Vollseite), Fenster/`window` (`scrollFormToTop`) und —
   * im schwebenden Fenster — ein `overflow: auto`-`<div>` zwischen beiden.
   *
   * Ohne Animation (`"auto"`): der Inhalt ist beim Schrittwechsel vollständig
   * ausgetauscht, und eine laufende Smooth-Animation bricht jeder
   * konkurrierende Scroll still ab — der Sprung bliebe mal aus und mal nicht.
   */
  const ersterSchrittRender = useRef(true);
  useEffect(() => {
    // Beim ersten Rendern gibt es nichts zurückzusetzen; ein Entwurf, der auf
    // einem späteren Schritt wieder aufsetzt, darf die Seite nicht anspringen.
    if (ersterSchrittRender.current) {
      ersterSchrittRender.current = false;
      return;
    }
    const el = mainRef.current;
    // `Element.scrollTo` fehlt in jsdom — ein Sprung darf nie werfen.
    if (el && typeof el.scrollTo === "function") {
      el.scrollTo({ top: 0, behavior: "auto" });
    }
    scrollFormToTop("auto");
    scrollScrollableAncestorsToTop(shellRef.current, "auto");
  }, [stepIdx]);

  const goPrev = () => {
    setStepIdx((s) => Math.max(0, s - 1));
  };

  const goNext = () => {
    setStepIdx((s) => Math.min(totalSteps - 1, s + 1));
  };

  const handleSelect = (sIdx: number, ssIdx: number) => {
    let cursor = 0;
    for (let i = 0; i < sIdx; i += 1) cursor += sections[i].subSteps.length;
    setStepIdx(cursor + ssIdx);
  };

  const jumpToStepKey = (key: string) => {
    const idx = steps.findIndex((s) => s.key === key);
    if (idx >= 0) setStepIdx(idx);
  };

  const handleWelcomeComplete = () => {
    setShowWelcome(false);
    if (token) {
      try {
        window.localStorage.setItem(WELCOME_LS_PREFIX + token, "1");
      } catch {}
    }
  };

  const handleSaveDraft = async () => {
    if (!token) return;
    setSavingDraft(true);
    try {
      const payload = {
        answers,
        yearsBack,
        stepIdx,
        savedAt: new Date().toISOString(),
      };
      try {
        window.localStorage.setItem(draftKey(token), JSON.stringify(payload));
        setDraftSavedAt(payload.savedAt);
      } catch {}
    } finally {
      window.setTimeout(() => setSavingDraft(false), 250);
    }
  };

  const handleSubmit = async () => {
    if (isEmbedded) {
      if (!onSubmitEmbedded) return;
      setSubmitting(true);
      try {
        await onSubmitEmbedded(answers);
      } finally {
        setSubmitting(false);
      }
      return;
    }
    if (!token) return;
    setSubmitting(true);
    try {
      const res = await fetch(apiUrl(`public-form/${token}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ answers, yearsBack, version: "kennzahlen-step-v1" }),
      });
      if (res.ok) {
        try {
          window.localStorage.removeItem(draftKey(token));
        } catch {}
        setSubmitOk(true);
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (isPublic && loading) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Formular wird geladen…</div>
        </div>
      </div>
    );
  }
  if (isPublic && !valid) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <div className={styles.pageStateBody}>Der Link ist ungültig oder abgelaufen.</div>
        </div>
      </div>
    );
  }
  if (isPublic && submitOk) {
    return (
      <div className={styles.pageState}>
        <div className={styles.pageStateCard}>
          <h2 className={styles.pageStateTitle}>Vielen Dank</h2>
          <p className={styles.pageStateBody}>
            Ihre Kennzahlen wurden übermittelt. Wir melden uns mit den nächsten Schritten.
          </p>
        </div>
      </div>
    );
  }

  if (isPublic && showWelcome) {
    return <KennzahlenWelcomeFlow companyName={companyName} onComplete={handleWelcomeComplete} />;
  }

  const step = steps[stepIdx];
  const section = sections[sectionIdx];
  const accent = sectionColorHex[section.color];
  const heroKind: KennzahlenHeroKind =
    step.kind === "period"
      ? "period"
      : step.kind === "summary"
      ? "summary"
      : HERO_BY_KIND[step.section];

  const renderFieldList = (
    fields: typeof BASIS_FIELDS,
    year: number
  ): JSX.Element => (
    <div className={styles.questions}>
      {fields.map((f) => {
        const key = `${f.name}_${year}`;
        const v = answers[key] ?? "";
        const mode = isCurrencyField(f.fieldType)
          ? "currency"
          : isNumericField(f.name, f.fieldType)
          ? "number"
          : "text";
        return (
          <div key={f.name} className={styles.fieldBlock}>
            <span className={styles.fieldLabel}>{f.label}</span>
            <CurrencyInput
              value={v}
              onChange={(next) => setAnswer(key, next)}
              placeholder={f.placeholder}
              mode={mode}
              // Die Beschriftung darüber ist ein <span> ohne `htmlFor` — der
              // zugängliche Name muss deshalb am Feld selbst hängen. Das Jahr
              // gehört dazu: dieselbe Kennzahl steht je Jahr einmal im Formular.
              ariaLabel={`${f.label} ${year}`}
            />
          </div>
        );
      })}
    </div>
  );

  const renderMitarbeiter = (year: number): JSX.Element => {
    const avgKey = `avgConcurrentPersons_${year}`;
    const matrixKey = `employeesMatrix_${year}`;
    return (
      <div className={styles.questions}>
        <div className={styles.fieldBlock}>
          <span className={styles.fieldLabel}>
            Ø gleichzeitig tätige Personen
          </span>
          <span className={styles.fieldHint}>
            Wie viele Menschen arbeiten zur gleichen Zeit für Sie?
          </span>
          <CurrencyInput
            value={answers[avgKey] ?? ""}
            onChange={(next) => setAnswer(avgKey, next)}
            mode="number"
            placeholder="0"
            ariaLabel={`Ø gleichzeitig tätige Personen ${year}`}
          />
        </div>
        <div className={styles.matrixWrap}>
          <span className={styles.fieldLabel}>Mitarbeiter-Matrix</span>
          <span className={styles.fieldHint}>
            Tragen Sie die Anzahl Mitarbeiter pro Funktion und Beschäftigungsart ein.
            Spalten: V Vollzeit · T Teilzeit · A Auszubildende · H Hilfskräfte · P
            Praktikanten · M Minijobber · GF Geschäftsführer · F Freelancer.
          </span>
          <EmployeeMatrixGrid
            value={answers[matrixKey]}
            onChange={(next) => setAnswer(matrixKey, next)}
          />
        </div>
      </div>
    );
  };

  const renderSummary = (): JSX.Element => (
    <div className={styles.summaryGrid}>
      {selectedYears.map((year) => {
        const umsatz = answers[`revenueTotal_${year}`] ?? "";
        const wareneinsatz = answers[`cogsEur_${year}`] ?? "";
        const rohertrag = answers[`grossProfit_${year}`] ?? "";
        const facts: Array<[string, string]> = [
          ["Anlagevermögen", formatCurrency(answers[`fixedAssets_${year}`] ?? "")],
          ["Aufwendungen GF", formatCurrency(answers[`mgmtCost_${year}`] ?? "")],
          ["Leiharbeiter Kosten", formatCurrency(answers[`tempLaborCost_${year}`] ?? "")],
          ["Subunternehmer Kosten", formatCurrency(answers[`subcontractorCost_${year}`] ?? "")],
        ];
        return (
          <div key={year} className={styles.summaryCard}>
            <div className={styles.summaryHead}>
              <div className={styles.summaryYear}>Geschäftsjahr {year}</div>
              <button
                type="button"
                className={styles.summaryEdit}
                onClick={() => jumpToStepKey(`${year}-basis`)}
              >
                Bearbeiten →
              </button>
            </div>
            <div className={styles.kpiRow}>
              <div className={styles.kpiTile}>
                <div className={styles.kpiLabel}>Umsatz</div>
                <div className={styles.kpiValue}>{formatCurrency(umsatz)}</div>
              </div>
              <div className={styles.kpiTile}>
                <div className={styles.kpiLabel}>Wareneinsatz</div>
                <div className={styles.kpiValue}>{formatCurrency(wareneinsatz)}</div>
              </div>
              <div className={styles.kpiTile}>
                <div className={styles.kpiLabel}>Rohertrag</div>
                <div className={styles.kpiValue}>{formatCurrency(rohertrag)}</div>
              </div>
            </div>
            {facts.map(([label, value]) => (
              <div key={label} className={styles.rowFact}>
                <span className={styles.rowFactKey}>{label}</span>
                <span className={styles.rowFactValue}>{value}</span>
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );

  const renderStepBody = (): JSX.Element => {
    if (step.kind === "period") {
      return (
        <PeriodPicker
          yearsBack={yearsBack}
          onChange={setYearsBack}
          selectedYears={selectedYears}
          currentYear={currentYear}
        />
      );
    }
    if (step.kind === "summary") return renderSummary();
    if (step.section === "basis") return renderFieldList(BASIS_FIELDS, step.year);
    if (step.section === "risiko") return renderFieldList(RISIKO_FIELDS, step.year);
    return renderMitarbeiter(step.year);
  };

  const subStepTitle = (() => {
    if (step.kind === "period") return "Zeitraum festlegen";
    if (step.kind === "summary") return "Übersicht";
    return step.title;
  })();

  const stepDescription: string | undefined = (() => {
    if (step.kind === "period") {
      return "Bitte legen Sie fest, für wie viele zurückliegende Jahre Sie Daten erfassen möchten. Laufendes Jahr und Folgejahr sind immer enthalten.";
    }
    if (step.kind === "summary") {
      return "Überprüfen Sie Ihre Eingaben pro Geschäftsjahr. Über Bearbeiten gelangen Sie zurück zum jeweiligen Schritt.";
    }
    if (step.section === "basis") {
      return `Basis-Kennzahlen für das Geschäftsjahr ${step.year}.`;
    }
    if (step.section === "risiko") {
      return `Aufwendungen und Risikofelder für ${step.year}.`;
    }
    return `Mitarbeiterstruktur für ${step.year}.`;
  })();

  // Ein gemeinsamer Satz Verzeichnis-Props für beide Darstellungen — die
  // Sidebar links und das Panel hinter dem Knopf zeigen dasselbe.
  const tocProps = {
    sections,
    currentSectionIdx: sectionIdx,
    currentSubStepIdx: subStepIdx,
    onSelect: handleSelect,
    progressFraction: overallFraction,
    progressDetail: `${progress.answered}/${progress.total} Felder`,
    // Im Fenster ist die Kopfzeile des Formulars nicht sichtbar — der
    // Verzeichniskopf ist dort das einzige, was die Risikoart benennt.
    eyebrow: "Kennzahlen",
    heading: "Inhaltsverzeichnis",
    ariaLabel: "Inhaltsverzeichnis der Kennzahlen-Erfassung",
    // `farbschema` bleibt bewusst auf dem Default „abschnitte": die Pille über
    // der Schritt-Überschrift trägt hier in JEDER Darstellung die
    // Abschnittsfarbe (`sectionPill`, `backgroundColor: accent`) — pro
    // Geschäftsjahr eine andere. Ein neutrales Verzeichnis würde diesen Bezug
    // innerhalb desselben Formulars zerreißen. (Die Immobilie ist mit #1312 den
    // anderen Weg gegangen und hat dort AUCH die Pille auf eine Markenfarbe
    // umgestellt; das wäre hier eine optische Änderung am Formular selbst.)
  };

  return (
    // Anders als im KFZ-Formular wird hier KEIN `--toc-leiste-breite` gesetzt:
    // die Variable wertet `BottomBar` nur in ihrer Vollseiten-Darstellung aus
    // (`sticky` + Page-Chrome), und die eingebettete Leiste dieses Formulars
    // läuft ohne `sticky` im Fluss. Sie wäre hier also wirkungslos.
    <div
      ref={shellRef}
      className={mergeClasses(styles.shell, isEmbedded && styles.shellEmbedded)}
    >
      {!isEmbedded && (
        // Genau ein Auslöser je Kontext: das Verzeichnis hängt jetzt am
        // `TocMenu` im Formular, nicht mehr am Hamburger der TopBar.
        <TopBar customerName={companyName} menuMode="never" />
      )}
      <div className={mergeClasses(styles.body, isEmbedded && styles.bodyEmbedded)}>
        {tocLayout === "sidebar" && (
          <aside
            className={mergeClasses(styles.sidebarDesktop, isEmbedded && styles.sidebarSticky)}
            /*
              Eingebettet klebt das `<aside>` oben und hat deshalb keine Höhe
              aus dem Layout — ohne den gemessenen Wert endete die dunkle
              Leiste auf Inhaltshöhe. In der Vollseite streckt der
              Flex-Container das `<aside>` bereits korrekt.
            */
            style={isEmbedded && availableHeight != null ? { height: availableHeight } : undefined}
          >
            <Sidebar {...tocProps} maxHeight={isEmbedded ? availableHeight : undefined} />
          </aside>
        )}

        <main className={mergeClasses(styles.main, isEmbedded && styles.mainEmbedded)} ref={mainRef}>
          <div className={mergeClasses(styles.inner, isEmbedded && styles.innerEmbedded)}>
            {tocLayout === "menu" && (
              <TocMenu
                {...tocProps}
                maxHeight={availableHeight}
                kontextText={`Schritt ${stepIdx + 1} von ${totalSteps}`}
              />
            )}
            <div className={styles.sectionPill} style={{ backgroundColor: accent }}>
              {section.title}
            </div>
            <div className={styles.titleRow}>
              <h2 className={styles.title}>{subStepTitle}</h2>
              <StepHero kind={heroKind} />
            </div>
            {stepDescription && <p className={styles.description}>{stepDescription}</p>}
            <div>{renderStepBody()}</div>
            {isEmbedded && (
              <div style={{ marginTop: 24 }}>
                <BottomBar
                  onPrev={goPrev}
                  onNext={goNext}
                  onSubmit={handleSubmit}
                  onSaveDraft={() => {}}
                  canPrev={canPrev}
                  isLastStep={isLastStep}
                  submitting={submitting}
                  savingDraft={false}
                  draftSavedAt={null}
                  progressLabel={`Schritt ${stepIdx + 1} von ${totalSteps}`}
                  progressFraction={stepFraction}
                  submitLabel={embeddedSubmitLabel}
                  submittingLabel="Speichere…"
                  showSaveDraft={false}
                />
              </div>
            )}
          </div>
        </main>
      </div>

      {!isEmbedded && (
        <div
          className={mergeClasses(
            styles.bottomBarFixed,
            tocLayout === "sidebar" && styles.bottomBarFixedNebenSidebar,
          )}
        >
          <BottomBar
            onPrev={goPrev}
            onNext={goNext}
            onSubmit={handleSubmit}
            onSaveDraft={handleSaveDraft}
            canPrev={canPrev}
            isLastStep={isLastStep}
            submitting={submitting}
            savingDraft={savingDraft}
            draftSavedAt={draftSavedAt}
            progressLabel={`Schritt ${stepIdx + 1} von ${totalSteps} · ${section.title}`}
            progressFraction={stepFraction}
            submitLabel="Kennzahlen absenden"
          />
        </div>
      )}
    </div>
  );
};

const RisikoKennzahlenStepFormPublic = () => <RisikoKennzahlenStepForm chrome="public" />;

export { RisikoKennzahlenStepForm };
export default RisikoKennzahlenStepFormPublic;
